import { useQuery } from '@apollo/client';
import { getDataFromTree } from '@apollo/client/react/ssr';
import { NextPage } from 'next';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
// Hocks
import withApollo from 'hocs/withApollo';
// Api
import { GET_ARTICLE_BY_SLUG } from 'api/articles/queries';
// Types
import {
  GetArticleBySlug,
  GetArticleBySlugVariables,
} from 'api/articles/types/GetArticleBySlug';
import { ArticleStatus } from 'api/graphql-global-types';
// Helpers
import {
  buildArticleMetaDescription,
  buildArticleOpenGraph,
  getArticleImageAlt,
} from 'helpers/articleSeo';
import { getCanonicalUrl } from 'helpers/canonical';
import { generateArticleStructuredData } from 'helpers/structured-data';
import {
  ApolloPageContext,
  PageProps,
  getCleanQueryParam,
  markPageAsNotFound,
} from 'helpers/next-page';
// Layouts
import Head from 'layouts/Head/Head';
import Header from 'layouts/Header/Header';
import Main from 'layouts/Main/Main';
import Footer from 'layouts/Footer/Footer';
// UI
import FourDotsLoader from 'ui/FourDotsLoader/FourDotsLoader';
// Components
import News from 'components/News/News';
import NotFoundPage from 'components/NotFoundPage/NotFoundPage';
import RouteRedirect from 'components/RouteRedirect/RouteRedirect';

const NewsPageContent = (): JSX.Element => {
  const { query, asPath } = useRouter();
  const { athleteSlug, newsSlug } = query;

  const storeSlug = athleteSlug as string;

  const cleanNewsSlug = (newsSlug as string)?.split('?')[0];

  const { data } = useQuery<GetArticleBySlug, GetArticleBySlugVariables>(
    GET_ARTICLE_BY_SLUG,
    {
      variables: {
        input: {
          articleSlug: cleanNewsSlug,
          storeSlug,
        },
      },
      skip: !cleanNewsSlug || !storeSlug,
    }
  );

  const newsData = data?.getArticleBySlug;

  if (!newsData) {
    return <FourDotsLoader />;
  }

  if (newsData.status !== ArticleStatus.Active) {
    return <RouteRedirect to={`/${storeSlug}`} />;
  }

  const title = newsData.title || '';

  const metaTitle = `${title} | MILLIONS`;
  const metaDescription = buildArticleMetaDescription(newsData);
  const imageUrl = newsData.url || '';

  const canonicalUrl = getCanonicalUrl(asPath);
  const structuredData = generateArticleStructuredData(newsData, storeSlug);

  return (
    <>
      <Head
        title={metaTitle}
        description={metaDescription}
        image={imageUrl}
        imageAlt={getArticleImageAlt(newsData)}
        canonicalLink={canonicalUrl}
        structuredData={structuredData}
        article={buildArticleOpenGraph(newsData, storeSlug)}
      />
      <Header />
      <Main backgroundColor="white" withoutSidesPadding withHeaderOffset>
        <News news={newsData} storeSlug={storeSlug} />
      </Main>
      <Footer />
    </>
  );
};

export const NewsPage: NextPage<PageProps> = ({ isNotFound, redirectTo }) => {
  if (redirectTo) {
    return <RouteRedirect to={redirectTo} />;
  }

  if (isNotFound) {
    return <NotFoundPage />;
  }

  return <NewsPageContent />;
};

NewsPage.getInitialProps = async (
  ctx: ApolloPageContext
): Promise<PageProps> => {
  const athleteSlug = getCleanQueryParam(ctx.query.athleteSlug);
  const newsSlug = getCleanQueryParam(ctx.query.newsSlug);

  if (!ctx.apolloClient || !athleteSlug || !newsSlug) {
    return {};
  }

  try {
    const { data } = await ctx.apolloClient.query<
      GetArticleBySlug,
      GetArticleBySlugVariables
    >({
      query: GET_ARTICLE_BY_SLUG,
      variables: {
        input: {
          articleSlug: newsSlug,
          storeSlug: athleteSlug,
        },
      },
    });

    const article = data?.getArticleBySlug;

    if (!article) {
      markPageAsNotFound(ctx);
      return { isNotFound: true };
    }

    if (article.status !== ArticleStatus.Active) {
      const redirectTo = `/${athleteSlug}`;

      if (ctx.res) {
        ctx.res.writeHead(301, { Location: redirectTo });
        ctx.res.end();
        return { redirectTo };
      }

      return { redirectTo };
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: { section: 'ssr', page: 'news/[newsSlug]' },
    });
    markPageAsNotFound(ctx);
    return { isNotFound: true };
  }

  return {};
};

export default withApollo(NewsPage, { getDataFromTree });
